import { saveAs } from 'file-saver';
import { IImageLibraryItem, isLibraryDocument } from './types';
import { imageLibraryDisplayName } from './useImageLibraryQuery';

/**
 * Saves a library entry's original file. The two kinds are stored differently, so they download
 * differently.
 *
 * Documents live in private storage, so their url is a presigned one that already carries
 * `Content-Disposition: attachment` and the file name. Handing it to the browser is enough, and it has to
 * be: presigned urls are issued against the raw S3 endpoint, which sends no CORS headers, so fetching the
 * bytes would be blocked. This is the same thing `MediaContentDisplay` does for generated documents.
 *
 * Images live in public storage with no disposition header, so a cross-origin link would open the image
 * in a tab rather than save it, and the file name would be lost. Those bytes have to be fetched and saved
 * as a blob, which works because the public storage host does allow any origin.
 */
export async function downloadLibraryItem(item: IImageLibraryItem) {
	if (!item.url) {
		throw new Error('Library entry has no url to download');
	}

	const fileName = item.fileAttachment?.fileName || imageLibraryDisplayName(item) || 'download';

	if (isLibraryDocument(item)) {
		saveAs(item.url, fileName);
		return;
	}

	const response = await fetch(item.url);
	if (!response.ok) {
		throw new Error(`Download failed with ${response.status}`);
	}

	saveAs(await response.blob(), fileName);
}
